import { countryByCode2, countryByCode3, REGION_NAMES } from './countries';

/** Display formatting. Numbers and dates are always shown in English (en-US). */

const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumSignificantDigits: 3,
});
const moneyFull = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const day = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const dayShort = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const time = new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });

/** $2.45M, $132K, $8.92K */
export const formatMoney = (usd: number) => money.format(usd);
/** $12,450 */
export const formatMoneyFull = (usd: number) => moneyFull.format(usd);
/** 18.7K */
export const formatCount = (n: number) => compact.format(n);
/** 24 May 2026 */
export const formatDate = (iso: string) => day.format(new Date(iso));
/** 24 May */
export const formatDay = (d: Date) => dayShort.format(d);
/** 24 May 2026, 10:42 UTC */
export const formatDateTime = (iso: string) => `${day.format(new Date(iso))}, ${time.format(new Date(iso))} UTC`;

/** "3 days ago", "in 2 hours": coarse, for "last seen" style hints. */
export const formatRelative = (iso: string, now = Date.now()) => {
  const rtf = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });
  const diff = (new Date(iso).getTime() - now) / 1000;
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31536000],
    ['month', 2592000],
    ['week', 604800],
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
  ];
  for (const [unit, secs] of units) {
    if (Math.abs(diff) >= secs) return rtf.format(Math.round(diff / secs), unit);
  }
  return 'just now';
};

/** An ISO 3166-1 alpha-3 code's English name (src/utils/countries.ts); an unknown code shows as-is. */
export const countryName = (code: string) => countryByCode3(code)?.name ?? countryByCode2(code)?.name ?? REGION_NAMES[code] ?? code;

/** "Ana Pop" -> "AP"; falls back to the email's first letters. */
/** The domain of the placeholder address a lead added without an email gets (LEAD-7); the backend's NoEmailDomain. */
const NO_EMAIL_DOMAIN = 'no-email.invalid';

/** Whether an email is such a placeholder: nothing is ever sent to it, and it is never shown. */
export const isNoEmail = (email: string) => email.endsWith(`@${NO_EMAIL_DOMAIN}`);

/** A lead's email as shown: "" for a placeholder. */
export const shownEmail = (email: string) => (isNoEmail(email) ? '' : email);

/** What to call a lead: its name, else its email, else "Lead without email". */
export const leadTitle = (name: string, email: string) => name || shownEmail(email) || 'Lead without email';

export const initials = (name: string, email: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return email.slice(0, 2).toUpperCase();
};
